/**
 * LookupMergePreview — inline expansion above the lookup admin list when
 * a merge is initiated (Block 6.C.1; subcategories + specialties only).
 *
 * Two-step flow:
 *   1. Source picked → instructional banner ("Pick target row") fixes
 *      the list rows to "Pick as target" mode. Cancel returns the list
 *      to its normal state.
 *   2. Target picked → cascade preview pane renders here with Cancel /
 *      Confirm buttons. Confirm fires lookupsApi.merge{Sub|Spec} with
 *      expected_record_count from the source row's data; 409 →
 *      "records changed since preview" + Cancel-and-reload affordance.
 *
 * ADR-017 F1: not a modal Dialog (the F1 carve-out for merge-confirm is
 * an inline expansion, not a modal-overlay; ADR-024 D3 holds).
 * ADR-017 F7: confirmation gate is the right shape — merge IS destructive
 * (deletes the source lookup row).
 *
 * Not session-local-undoable per ADR-020 §1 + plan §2.5: the audit-trail
 * row IS the durable recovery trail. This component does not push an
 * undo entry on success.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LookupAdminRow } from '@/lib/types';
import { lookupsApi } from '@/lib/lookups';
import { formatRecordCount } from '@/lib/format';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { refreshFamily } from '@/lib/refreshFamily';

export interface LookupMergePreviewProps {
  tenantSlug: string;
  type: 'subcategories' | 'specialties' | 'highlights';
  source: LookupAdminRow;
  /** Set after user picks a target row from the list. */
  target: LookupAdminRow | null;
  invalidationKey: ReadonlyArray<unknown>;
  onCancel: () => void;
  onMergeComplete: () => void;
}

export function LookupMergePreview({
  tenantSlug,
  type,
  source,
  target,
  invalidationKey,
  onCancel,
  onMergeComplete,
}: LookupMergePreviewProps): ReactNode {
  const queryClient = useQueryClient();
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const contact = useSupportContact();

  // The merge (both rows and the count previewed), the type and the list
  // to refresh (the family) are read at the press and travel as the
  // variables. With no target picked the merge is refused inside the
  // mutation, as before.
  const mutation = useMutation({
    mutationFn: async (v: {
      body: { from_id: string; into_id: string; expected_record_count: number } | null;
      type: typeof type;
      family: readonly unknown[];
    }) => {
      if (v.body === null) {
        throw new Error('Target not picked');
      }
      if (v.type === 'subcategories') {
        return lookupsApi.mergeSubcategories(tenantSlug, v.body);
      }
      if (v.type === 'specialties') {
        return lookupsApi.mergeSpecialties(tenantSlug, v.body);
      }
      return lookupsApi.mergeHighlights(tenantSlug, v.body);
    },
    onSuccess: async (_res, v) => {
      await queryClient.invalidateQueries({ queryKey: v.family });
      onMergeComplete();
    },
    onError: (e: Error & { status?: number; body?: { error?: string; expected?: number; actual?: number } }, v) => {
      if (e.status === 409 && e.body?.error === 'record_count_changed') {
        setConflictMessage(
          `Record count changed since preview (was ${e.body.expected ?? v.body?.expected_record_count ?? '?'}, now ${e.body.actual ?? '?'}). ` +
            `Cancel and start the merge again to see the current count.`,
        );
      } else {
        setConflictMessage(pageWriteText(e, contact));
      }
    },
  });

  // Step 1: source picked, target not yet.
  if (target === null) {
    return (
      <div className="border-2 border-amber-400 bg-amber-50 rounded-md p-3 mb-3">
        <div className="text-sm font-semibold mb-1">
          Merge: {source.display_name} → ?
        </div>
        <div className="text-xs text-ink-soft mb-2">
          {formatRecordCount(source.record_count)} record
          {source.record_count === 1 ? '' : 's'} reference this row.
        </div>
        <div className="text-xs text-ink-soft mb-3">
          Pick a target row below; its record count will absorb the source's references.
          The source row is then deleted.
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            Cancel merge
          </button>
        </div>
      </div>
    );
  }

  // Step 2: source + target picked → confirm.
  return (
    <div className="border-2 border-amber-400 bg-amber-50 rounded-md p-3 mb-3">
      <div className="text-sm font-semibold mb-2">Confirm merge</div>
      <div className="text-sm mb-2">
        <span className="font-mono">{source.slug}</span>
        <span className="mx-2 text-amber-700">→</span>
        <span className="font-mono">{target.slug}</span>
      </div>
      <ul className="text-xs text-ink-soft list-disc pl-5 mb-3 space-y-1">
        <li>
          {formatRecordCount(source.record_count)} record
          {source.record_count === 1 ? '' : 's'} re-pointed to{' '}
          <span className="font-semibold">{target.display_name}</span>.
        </li>
        <li>
          Source row <span className="font-semibold">{source.display_name}</span> will be
          deleted.
        </li>
        <li>
          <span className="font-semibold">Not session-undoable.</span>
        </li>
      </ul>
      {conflictMessage !== null && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="lookup-merge-error">
          ⚠ {conflictMessage}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() =>
            mutation.mutate({
              body:
                target === null
                  ? null
                  : { from_id: source.id, into_id: target.id, expected_record_count: source.record_count },
              type,
              family: refreshFamily(invalidationKey),
            })
          }
          disabled={mutation.isPending}
          className="min-h-11 px-3 py-1 text-sm bg-amber-600 text-cream rounded-md hover:bg-amber-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {mutation.isPending ? 'Merging…' : 'Confirm merge'}
        </button>
      </div>
    </div>
  );
}
